import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { dossierAPI, messageAPI } from '@/utils/api';
import { toast } from 'sonner';
import Header from '@/components/Header';

const DossierDetailPage = () => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [dossierId]);

  const fetchData = async () => {
    try {
      const [dossierResponse, messagesResponse] = await Promise.all([
        dossierAPI.get(dossierId),
        messageAPI.getMessages(dossierId)
      ]);
      setDossier(dossierResponse.data);
      setMessages(messagesResponse.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement du dossier');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await messageAPI.getMessages(dossierId);
      setMessages(response.data);
    } catch (error) {
      console.error('Messages fetch error:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await messageAPI.send({
        dossier_id: dossierId,
        content: newMessage
      });
      setNewMessage('');
      toast.success('Message envoyé');
      fetchMessages();
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="loader"></div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <p>Dossier introuvable</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Dossier Info */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-4">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#1e3a8a' }}>
                Informations du dossier
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">ID Dossier</p>
                  <p className="font-medium text-gray-800">#{dossier.id.slice(0, 8)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Score</p>
                  {dossier.score !== null ? (
                    <p className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
                      {dossier.score}/100
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">En cours...</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fichiers</p>
                  <p className="font-medium text-gray-800">
                    {dossier.files?.length || 0} document(s)
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      dossier.payment_status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {dossier.payment_status === 'paid' ? 'Expert activé' : 'En attente'}
                  </span>
                </div>
                <div>
                  <Button
                    data-testid="view-score-button"
                    className="w-full mt-4"
                    onClick={() => navigate(`/score/${dossierId}`)}
                    style={{ backgroundColor: '#1e3a8a', color: 'white' }}
                  >
                    Voir l'analyse
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="lg:col-span-2">
            <div className="card p-6">
              <h3 className="text-xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                Messagerie Expert
              </h3>

              {dossier.payment_status !== 'paid' ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">
                    Activez l'accès expert pour communiquer avec un conseiller juridique.
                  </p>
                  <Button
                    data-testid="activate-expert-button"
                    onClick={() => navigate(`/score/${dossierId}`)}
                    style={{ backgroundColor: '#1e3a8a', color: 'white' }}
                  >
                    Activer l'accès (9,99€)
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    data-testid="messages-container"
                    className="space-y-4 mb-6 max-h-96 overflow-y-auto"
                    style={{ minHeight: '300px' }}
                  >
                    {messages.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>Aucun message pour le moment.</p>
                        <p className="text-sm mt-2">Commencez la conversation avec votre expert.</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          data-testid={`message-${message.id}`}
                          className={`flex items-start space-x-3 ${
                            message.sender_type === 'admin' ? 'flex-row' : 'flex-row-reverse space-x-reverse'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              message.sender_type === 'admin' ? 'bg-blue-100' : 'bg-gray-200'
                            }`}
                          >
                            {message.sender_type === 'admin' ? (
                              <FileText size={20} color="#1e3a8a" />
                            ) : (
                              <User size={20} color="#6b7280" />
                            )}
                          </div>
                          <div className="flex-1 max-w-md">
                            <div
                              className={`p-4 rounded-lg ${
                                message.sender_type === 'admin'
                                  ? 'bg-blue-50 border border-blue-200'
                                  : 'bg-gray-100 border border-gray-200'
                              }`}
                            >
                              <p className="text-sm font-semibold mb-1" style={{ color: '#1e3a8a' }}>
                                {message.sender_name}
                              </p>
                              <p className="text-gray-800">{message.content}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                {formatDate(message.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <form data-testid="message-form" onSubmit={handleSendMessage} className="border-t pt-4">
                    <Textarea
                      data-testid="message-input"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrivez votre message..."
                      rows={3}
                      className="mb-3"
                    />
                    <Button
                      data-testid="send-message-button"
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      style={{ backgroundColor: '#1e3a8a', color: 'white' }}
                    >
                      <Send className="mr-2" size={20} />
                      {sending ? 'Envoi...' : 'Envoyer'}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DossierDetailPage;